<template>
  <section class="mb-3 pb-4 pb-md-0">
    <div class="container px-0">
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div
              class="card-body px-md-4 py-md-5 rounded-4 transition-shadow
                box-shadow-green-300 box-shadow-hover-green-300"
            >
              <div class="row">
                <div class="col-2">
                  <v-icon
                    icon="chevron-left"
                    class="cursor-pointer"
                    width="19"
                    height="19"
                    @click="() => $router.back()"
                  />
                </div>
                <div class="col-8">
                  <div
                    v-if="!isNewCompany"
                    class="h5 text-center mb-3"
                  >
                    Mon entreprise
                  </div>
                  <div
                    v-if="isNewCompany"
                    class="h5 text-center mb-3"
                  >
                    Ma nouvelle entreprise
                  </div>
                </div>
                <div class="col-12 col-md-10 mx-auto">
                  <v-input
                    :input-disabled="true"
                    input-id="company-name"
                    label="Raison sociale"
                    :module-name="moduleName"
                    getter-name="get"
                    :getter-function="({ name }) => isNewCompany ? newCompany.name : name"
                    :path="pathName"
                    :input-validation="validateCompanyName"
                    class="my-3"
                  />
                  <v-input
                    :input-disabled="true"
                    input-id="company-siret"
                    label="Numéro de SIRET"
                    mask="siret"
                    :module-name="moduleName"
                    getter-name="get"
                    :getter-function="({ siret }) => isNewCompany ? newCompany.siret : siret"
                    :input-validation="validateSiret"
                    :path="pathSiret"
                    class="my-3"
                  />
                  <v-place
                    :input-disabled="true"
                    input-id="company-address"
                    label="Adresse du siège social"
                    :module-name="moduleName"
                    getter-name="get"
                    :getter-function="({ address }) => isNewCompany ? newCompany.address : address"
                    :path="pathAddress"
                    error-message="Adresse incomplète"
                    class="my-3"
                  />
                  <v-autocomplete
                    :disabled="true"
                    class="mt-3"
                    label="Activité principale"
                    :list="researchResult"
                    :value="getMainActivity"
                    :module-name="moduleName"
                    :path="pathMainActivity"
                    @getlist="searchResults"
                    @setitem="setActivity"
                  />
                  <div
                    v-if="getMainActivity"
                    class="py-2 bg-gray-300 fw-bold px-2 text-blue-700 mt-1 mb-3"
                    style="border-radius: 25px"
                  >
                    <div class="d-flex flex-row">
                      <div class="flex text-truncate me-2">
                        {{ getMainActivity }}
                      </div>
                      <div
                        style="min-width: 16px"
                        class="ms-auto"
                      >
                        <v-icon icon="checkmark-green-600" />
                      </div>
                    </div>
                  </div>
                  <v-autocomplete
                    :disabled="true"
                    label="Code APE ou NAF"
                    :list="apeList"
                    :value="getCodeNaf"
                    class="mt-3"
                    :module-name="moduleName"
                    :path="pathCodeNaf"
                    @getlist="getApeList"
                    @setitem="setCodeNaf"
                  />
                  <div
                    v-if="getCodeNaf"
                    class="py-2 bg-gray-300 fw-bold px-2 text-blue-700 mt-1 mb-3"
                    style="border-radius: 25px"
                  >
                    <div class="d-flex flex-row">
                      <div class="flex text-truncate me-2">
                        {{ getCodeNaf }}
                      </div>
                      <div
                        style="min-width: 16px"
                        class="ms-auto"
                      >
                        <v-icon icon="checkmark-green-600" />
                      </div>
                    </div>
                  </div>
                  <v-select
                    disabled
                    input-id="company-legal-form"
                    label="Forme juridique"
                    :module-name="moduleName"
                    getter-name="get"
                    :getter-function="({ formeJuridique }) =>
                      isNewCompany ? newCompany.formeJuridique : formeJuridique"
                    :list="legalForms"
                    :path="pathFormeJuridique"
                    class="my-3"
                    :input-disabled="true"
                  />
                  <v-input
                    input-id="company-revenue"
                    label="Chiffre d'affaires"
                    mask="number"
                    placeholder="100 000"
                    :module-name="moduleName"
                    getter-name="get"
                    :getter-function="({ reportedRevenue }) =>
                      isNewCompany ? newCompany.reportedRevenue : reportedRevenue"
                    :path="pathReportedRevenue"
                    class="my-3"
                  />
                  <v-input
                    input-id="company-foreign-revenue"
                    label="Dont chiffre d'affaires réalisé à l'étranger"
                    mask="number"
                    placeholder="100 000"
                    :module-name="moduleName"
                    getter-name="get"
                    :getter-function="({ foreignRevenue }) =>
                      isNewCompany ? newCompany.foreignRevenue : foreignRevenue"
                    :path="pathForeignRevenue"
                    class="my-3"
                  />
                </div>
              </div>
              <div class="col-12 col-md-10 mx-auto mt-3">
                <v-loader
                  v-if="!isNewCompany"
                  :next="updateCompany"
                  title="Valider les modifications"
                  class="w-100"
                  :disabled="!validation"
                />
                <v-loader
                  v-if="isNewCompany"
                  :next="createCompany"
                  title="Ajouter l'entreprise"
                  class="w-100"
                  :disabled="!validation"
                />
                <v-loader
                  v-if="!isNewCompany"
                  :next="undo"
                  title="Annuler"
                  color="block"
                  class="w-100 mt-1"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import { debounce, isEmpty, get } from 'lodash-es';

import { AutoComplete } from '@/components/common/input';
import {
  validateNewCompany, validateSiret, validateCompanyName,
} from '@/utils/validators';

import Input from '@/components/common/input/Input2.vue';
import Place from '@/components/common/input/Place2.vue';
import CustomSelect from '@/components/common/input/CustomSelect2.vue';

export default {
  name: 'AccountCompany',
  components: {
    'v-input': Input,
    'v-place': Place,
    'v-select': CustomSelect,
    'v-autocomplete': AutoComplete,
  },
  data() {
    return {
      debouncer: { cancel: () => {} },
    };
  },
  computed: {
    ...mapGetters({
      legalForms: 'rater/legalForms',
      apeList: 'companies/getApeList',
      companyId: 'company/companyId',
      company: 'company/get',
      newCompany: 'companies/company',
      researchResult: 'activities/researchResult',
    }),
    moduleName() {
      if (this.$route.path.includes('/new')) {
        return 'companies';
      }
      return 'company';
    },
    pathName() {
      if (this.$route.path.includes('/new')) {
        return 'company.name';
      }
      return 'name';
    },
    pathSiret() {
      if (this.$route.path.includes('/new')) {
        return 'company.siret';
      }
      return 'siret';
    },
    pathAddress() {
      if (this.$route.path.includes('/new')) {
        return 'company.address';
      }
      return 'address';
    },
    pathCodeNaf() {
      if (this.$route.path.includes('/new')) {
        return 'company.codeNaf';
      }
      return 'codeNaf';
    },
    pathMainActivity() {
      if (this.$route.path.includes('/new')) {
        return 'company.codeNaf';
      }
      return 'mainActivity';
    },
    pathFormeJuridique() {
      if (this.$route.path.includes('/new')) {
        return 'company.formeJuridique';
      }
      return 'formeJuridique';
    },
    pathReportedRevenue() {
      if (this.$route.path.includes('/new')) {
        return 'company.reportedRevenue';
      }
      return 'reportedRevenue';
    },
    pathForeignRevenue() {
      if (this.$route.path.includes('/new')) {
        return 'company.foreignRevenue';
      }
      return 'foreignRevenue';
    },
    getCodeNaf() {
      if (this.$route.path.includes('/new')) {
        return get(this.newCompany, 'codeNaf.name');
      }
      return get(this.company, 'codeNaf.name');
    },
    getMainActivity() {
      if (this.$route.path.includes('/new')) {
        return get(this.newCompany, 'mainActivity.name');
      }
      return get(this.company, 'mainActivity.name');
    },
    isNewCompany() {
      if (this.$route.path.includes('/new')) {
        return true;
      }
      return false;
    },
    validation() {
      if (this.$route.path.includes('/new')) {
        return validateNewCompany(this.newCompany);
      }
      return true;
    },
    validators() {
      const {
        name, siret, address, codeNaf, reportedRevenue, formeJuridique,
      } = this.company;
      const haveAddress = Object.values(address)
        .join('').length > 0;
      const isRevenueValid = reportedRevenue !== null && reportedRevenue > 0;
      return [
        { title: 'Raison sociale', isValid: !isEmpty(name) },
        { title: 'Code APE ou NAF', isValid: !isEmpty(codeNaf) && !isEmpty(codeNaf.code) },
        { title: 'Code APE ou NAF', isValid: !isEmpty(formeJuridique) && !isEmpty(formeJuridique.code) },
        { title: 'Siret', isValid: !isEmpty(siret) },
        { title: 'Chiffre d\'affaires', isValid: isRevenueValid },
        { title: 'Adresse', isValid: haveAddress },
      ];
    },
    isNotValid() {
      return this.validators.some(({ isValid }) => !isValid);
    },
  },
  beforeDestroy() {
    this.$store.dispatch('company/setCompanyById', this.companyId);
  },
  mounted() {
    this.$store.dispatch('activities/getAllActivities');
  },
  methods: {
    searchResults(value) {
      this.debouncer.cancel();
      const setValue = () => {
        this.$store.dispatch('activities/search', { value });
      };
      this.debouncer = debounce(setValue, 500);
      this.debouncer();
    },
    getApeList(term) {
      this.debouncer.cancel();
      const toBeCalled = () => {
        this.$store.dispatch('companies/setApeList', term);
      };
      this.debouncer = debounce(
        toBeCalled,
        1000,
      );
      this.debouncer();
    },
    setCodeNaf(item) {
      if (this.$route.path.includes('/new')) {
        this.$store.dispatch('companies/setCodeNaf', item);
      } else {
        this.$store.dispatch('company/setCodeNaf', item);
      }
      this.isNafValid = true;
    },
    setActivity(activity) {
      if (this.$route.path.includes('/new')) {
        this.$store.dispatch('companies/setMainActivity', activity);
      } else {
        this.$store.dispatch('company/setMainActivity', activity);
      }
    },
    async updateCompany() {
      await this.$store.dispatch('company/updateCompany', this.company.id);
      return this.$store.dispatch('ui/showModalAccount', { value: true, type: 'company' });
    },
    async createCompany() {
      await this.$store.dispatch('companies/addCompanyMember', this.newCompany);
      return this.$store.dispatch('ui/showModalAccount', { value: true, type: 'createCompany' });
    },
    async undo() {
      await this.$store.dispatch('company/setCompanyById', this.companyId);
      return this.$router.push('/client/account/companies');
    },
    validateSiret,
    validateCompanyName,
  },
};
</script>
